import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ProfileSetup from "@/pages/ProfileSetup";
import Dashboard from "@/pages/Dashboard";
import DevotionalDisplay from "@/pages/DevotionalDisplay";
import Library from "@/pages/Library";
import CollectiveLibrary from "@/pages/CollectiveLibrary";
import Profile from "@/pages/Profile";
import WisdomJourney from "@/pages/WisdomJourney";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import { Toaster } from "@/components/ui/sonner";
import { authToken } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Restore token from localStorage on app load
const token = authToken.get();
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

function AuthWrapper({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const resp = await axios.get(`${API}/auth/me`);
        setUser(resp.data);
        if (location.pathname === '/') {
          const profileResp = await axios.get(`${API}/profile`);
          if (profileResp.data) {
            navigate('/dashboard');
          } else {
            navigate('/profile-setup');
          }
        }
      } catch (err) {
        // Not authenticated
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate, location]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="cosmic-loader"></div>
        <p>Opening the wisdom portal...</p>
      </div>
    );
  }

  return children({ user, setUser });
}

function ProtectedRoute({ children, user }) {
  if (!user) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthWrapper>
          {({ user, setUser }) => (
            <Routes>
              <Route path="/" element={<Landing user={user} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/profile-setup"
                element={
                  <ProtectedRoute user={user}>
                    <ProfileSetup user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute user={user}>
                    <Dashboard user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devotional"
                element={
                  <ProtectedRoute user={user}>
                    <DevotionalDisplay user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devotional/:id"
                element={
                  <ProtectedRoute user={user}>
                    <DevotionalDisplay user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/library"
                element={
                  <ProtectedRoute user={user}>
                    <Library user={user} setUser={setUser} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/collective-library"
                element={
                  <ProtectedRoute user={user}>
                    <CollectiveLibrary user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute user={user}>
                    <Profile user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wisdom-journey"
                element={
                  <ProtectedRoute user={user}>
                    <WisdomJourney user={user} />
                  </ProtectedRoute>
                }
              />
              <Route path="/privacy" element={<PrivacyPolicy />} />
            </Routes>
          )}
        </AuthWrapper>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;